import { Direction } from '@modules/common/types';
import {
  removeCellFromRow,
  resetLayout,
} from '@modules/dashboard/components/Layout/DashboardCell/helpers';
import { RowResizerAddContentButton } from '@modules/dashboard/components/Layout/RowResizerAddContentButton';
import {
  ROW_COL_SPAN,
  UPDATE_DASHBOARD_IN_PROGRESS_INFO,
} from '@modules/dashboard/constants';
import {
  getDashboardRow,
  getMaxCellsMinHeight,
  getNewRowID,
  stopAutoscroll,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, BoxProps, Tooltip } from '@mui/material';
import { DashboardRow } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { MouseEvent, useEffect, useState } from 'react';

const ROW_RESIZER_ADD_CONTENT_BTN_CLASS = 'row-resizer-add-content-button';

interface DashboardRowResizerParams {
  row: DashboardRow;
  omMouseDown?: (event: MouseEvent) => void;
  sx?: BoxProps['sx'];
  shouldAddAtTheTop?: boolean;
  isResizable?: boolean;
  shouldAllowDrop?: boolean;
  shouldAllowAddContent?: boolean;
  shouldAlwaysShowLine?: boolean;
}

export function DashboardRowResizer({
  row,
  sx,
  omMouseDown,
  shouldAddAtTheTop = false,
  isResizable = true,
  shouldAllowDrop = true,
  shouldAllowAddContent = true,
  shouldAlwaysShowLine = false,
}: DashboardRowResizerParams) {
  const [left, setLeft] = useState(0);
  const { upsertDashboard } = useUpsertDashboard();
  const [isResizerActive, setIsResizerActive] = useState(false);
  const [isAddContentBtnActive, setIsAddContentBtnActive] = useState(false);
  const [rowElement, setRowElement] = useState<Element | null>(null);
  const rows = useDashboardStore(({ metadata }) => metadata.layout.rows);
  const [shouldShowDropIndicator, setShouldShowDropIndicator] = useState(false);
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );
  const cellMinimumHeights = cellMinimumHeightsByRowID[row.id];

  const isUpdateDashboardInProgress = useDashboardStore(
    ({ visual }) => visual.isUpdateDashboardInProgress,
  );

  useEffect(() => {
    setRowElement(document.querySelector(`[data-id='${row.id}']`));
  }, [row.id]);

  useEffect(() => {
    const onMouseUp = () => {
      setIsResizerActive(false);
    };

    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  function onDragOver(eve: React.DragEvent) {
    eve.preventDefault();
    setShouldShowDropIndicator(true);
  }

  function onDragLeave(eve: React.DragEvent) {
    setShouldShowDropIndicator(false);
  }

  async function handleCellDropInBottomArea({
    draggedCellRowID,
    draggedCellID,
  }: {
    draggedCellRowID: string;
    draggedCellID: string;
  }) {
    const updatedRows = cloneDeep(rows);
    const draggedCellRow = getDashboardRow({
      rowID: draggedCellRowID,
      rows: updatedRows,
    });

    if (!draggedCellRow) {
      return;
    }

    const draggedCellRowIdx = updatedRows.findIndex(
      (row) => row.id === draggedCellRowID,
    );
    const draggedCellIdx = draggedCellRow.cells.findIndex(
      (cell) => cell.id === draggedCellID,
    );

    const draggedCell = draggedCellRow.cells[draggedCellIdx];
    const newRowID = getNewRowID();

    const newRow: DashboardRow = {
      id: newRowID,
      height: 0,
      cells: [
        {
          id: draggedCellID,
          columnSpan: ROW_COL_SPAN,
          contentID: draggedCell.contentID,
        },
      ],
    };

    let dropTargetRowIdx = updatedRows.findIndex(({ id }) => id === row.id);
    dropTargetRowIdx = shouldAddAtTheTop
      ? dropTargetRowIdx
      : dropTargetRowIdx + 1;

    updatedRows.splice(dropTargetRowIdx, 0, newRow);
    removeCellFromRow(draggedCellRow.cells, draggedCellIdx);

    const layout = { rows: updatedRows };
    resetLayout(layout);

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: { layout },
    });

    const clonedCellMinimumHeightsByRowID = cloneDeep(
      cellMinimumHeightsByRowID,
    );

    const height =
      clonedCellMinimumHeightsByRowID[draggedCellRowID][draggedCellID];
    delete clonedCellMinimumHeightsByRowID[draggedCellRowID][draggedCellID];
    clonedCellMinimumHeightsByRowID[newRowID] = {
      [draggedCellID]: height,
    };

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        dragItemData: null,
        dropTargetData: null,
        cellMinimumHeightsByRowID: clonedCellMinimumHeightsByRowID,
      },
    });

    await upsertDashboard({
      dashboardProps: {
        metadataV2: {
          layout,
        },
      },
    });

    const isDraggedFromTop = draggedCellRowIdx < dropTargetRowIdx;
    const from = isDraggedFromTop ? 'top' : 'bottom';
    const to = isDraggedFromTop ? 'bottom' : 'top';

    trackDashboardEvent({
      eventName: 'Dashboard Widget Reorder',
      from,
      to,
    });
  }

  function onDrop(eve: React.DragEvent) {
    eve.preventDefault();
    setShouldShowDropIndicator(false);
    stopAutoscroll();

    const { cellID: draggedCellID, rowID: draggedCellRowID } = JSON.parse(
      eve.dataTransfer.getData('application/json'),
    );

    handleCellDropInBottomArea({
      draggedCellRowID,
      draggedCellID,
    });
  }

  function onMouseMove(eve: React.MouseEvent) {
    if (!rowElement) {
      return null;
    }

    const offsetX = -15;
    const rect = rowElement.getBoundingClientRect();
    const cursorRelativePositionX = eve.clientX - rect.left;
    const rowWidth = rect.width;

    if (
      cursorRelativePositionX - offsetX >= 0 &&
      cursorRelativePositionX + offsetX <= rowWidth
    ) {
      setLeft(cursorRelativePositionX + offsetX);
    }
  }

  if (!rowElement) {
    return null;
  }

  const addContentButton = shouldAllowAddContent ? (
    <RowResizerAddContentButton
      rowID={row.id}
      className={ROW_RESIZER_ADD_CONTENT_BTN_CLASS}
      onActive={() => setIsAddContentBtnActive(true)}
      onInactive={() => setIsAddContentBtnActive(false)}
      cellInsertDirection={shouldAddAtTheTop ? Direction.Top : Direction.Bottom}
    />
  ) : null;

  const lineElement = (
    <Box
      sx={{
        position: 'absolute',
        top: '8px',
        width: '100%',
        height: '4px',
        boxSizing: 'border-box',
        backgroundColor: 'none',
        borderRadius: '5px',
        opacity: shouldShowDropIndicator ? 0.75 : 0.2,
        transition: 'background-color .25s ease 0s',
      }}
    />
  );

  const handleElement =
    !isResizable || shouldShowDropIndicator || isAddContentBtnActive ? null : (
      <Tooltip
        title={
          isUpdateDashboardInProgress ? UPDATE_DASHBOARD_IN_PROGRESS_INFO : null
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '7px',
            height: '6px',
            width: '32px',
            left,
            boxSizing: 'border-box',
            backgroundColor: shouldShowDropIndicator
              ? (theme) => theme.palette.primary.main
              : 'none',
            borderRadius: '5px',
            transition: 'background-color .25s ease 0s',
          }}
        />
      </Tooltip>
    );

  const getCursor = () => {
    if (!isResizable) {
      return 'initial';
    }

    if (row.height > getMaxCellsMinHeight(cellMinimumHeights)) {
      return 'ns-resize';
    }

    return 's-resize';
  };

  return (
    <Box
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      sx={{
        position: 'relative',
        height: '20px',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        '&:hover': {
          [`.${ROW_RESIZER_ADD_CONTENT_BTN_CLASS}`]: {
            opacity: 1,
          },
        },
        ...sx,
      }}
    >
      {addContentButton}
      <Box
        onMouseDown={
          shouldAllowDrop
            ? (eve) => {
                setIsResizerActive(true);
                omMouseDown?.(eve);
              }
            : undefined
        }
        onMouseMove={shouldAllowDrop ? onMouseMove : undefined}
        sx={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          position: 'relative',
          pt: '2px',
          cursor: getCursor(),
          '> *': {
            backgroundColor:
              isResizerActive ||
              shouldShowDropIndicator ||
              isAddContentBtnActive
                ? (theme) => theme.palette.primary.main
                : 'none',
          },
          '&:hover': {
            '> *': {
              backgroundColor:
                shouldShowDropIndicator ||
                shouldAllowDrop ||
                shouldAlwaysShowLine
                  ? (theme) => theme.palette.primary.main
                  : 'none',
            },
          },
        }}
      >
        {lineElement}
        {handleElement}
      </Box>
    </Box>
  );
}
